import { useEffect, useState } from "react";

/**
 * <AuthImage>
 * ------------
 * Renders an `<img>` whose bytes come from an authenticated `fetch()` →
 * `Blob` → `URL.createObjectURL()`. Use this anywhere you'd normally write
 *
 *    <img src="${BACKEND_URL}/api/some-auth-endpoint" />
 *
 * Why this exists: native `<img src>` requests CANNOT carry an
 * `Authorization: Bearer` header. We rely on the httpOnly `access_token`
 * cookie as a fallback, but that cookie is unreliable across the prod
 * cross-domain boundary (preview → realtyvaultmgr.com), iOS Safari ITP,
 * and post-`withCredentials`-removal sessions. The result is broken images
 * in production. This component fetches the bytes with the same Bearer
 * header used by every other API call, converts to a Blob, and uses the
 * resulting `blob:` URL as the `<img src>` — auth-safe everywhere.
 *
 * Bugs this fixes (and would have prevented):
 *   - Feb 2026: PropertyImage broken in prod (`<img src=/api/properties/image>`)
 *   - Feb 2026: Deal PhotoSlot broken on edit reopen (`<img src=/api/deals/.../photos/...>`)
 *
 * Props:
 *   - src: absolute or backend-relative URL (e.g. `/api/deals/{id}/photos/{pid}`)
 *   - cacheBust: any scalar — when it changes, the image is re-fetched
 *   - placeholder / errorFallback: ReactNodes shown during load / on failure
 *   - All other props (className, alt, onClick, ...) are passed through to <img>
 */
export default function AuthImage({
  src,
  cacheBust,
  placeholder,
  errorFallback,
  className,
  alt = "",
  ...imgProps
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!src) { setBlobUrl(null); return; }
    let cancelled = false;
    let created = null;
    setErr(false);
    setBlobUrl(null);
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const absolute = /^https?:\/\//i.test(src) ? src : `${base}${src.startsWith("/") ? "" : "/"}${src}`;
        const sep = absolute.includes("?") ? "&" : "?";
        const url = cacheBust ? `${absolute}${sep}v=${encodeURIComponent(cacheBust)}` : absolute;
        const token = localStorage.getItem("rv_token");
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (!res.ok) { setErr(true); return; }
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src, cacheBust]);

  if (err && errorFallback !== undefined) return errorFallback;
  if (!blobUrl) return placeholder !== undefined ? placeholder : <div className={className} />;

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setErr(true)}
      {...imgProps}
    />
  );
}
